import {
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  Timestamp,
  deleteField,
} from 'firebase/firestore';

/**
 * Version de schéma par défaut posée sur tout nouveau document écrit par le SDK.
 * Sert de socle aux migrations futures (@taki/migrations).
 */
export const CURRENT_SCHEMA_VERSION = 1 as const;

/**
 * Convertit en `Date` les valeurs temporelles lues depuis Firestore.
 * Firestore renvoie des `Timestamp` ; on expose des `Date` côté SDK.
 */
export function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Convertit une valeur temporelle vers un `Timestamp` Firestore (ou `null`).
 */
export function toTimestamp(value: Date | null | undefined): Timestamp | null {
  if (value == null) return null;
  return Timestamp.fromDate(value);
}

/**
 * Un objet "nu" (map Firestore), par opposition aux instances de classe du SDK
 * Firestore (`Timestamp`, `GeoPoint`, `Bytes`, `DocumentReference`...) qu'il ne
 * faut SURTOUT PAS traverser : les recopier champ a champ les casserait.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Convertit RECURSIVEMENT tout `Timestamp` d'un document en `Date`.
 *
 * Pourquoi a la racine : les schemas Zod du data-model declarent `z.date()`
 * pour TOUTES les dates, standard comme metier (`receivedAt`, `performedAt`,
 * `startAt`...). Convertir seulement les trois champs standard laissait les
 * dates metier arriver en `Timestamp` brut chez Zod, qui refusait le document
 * a la RELECTURE (l'ecriture, elle, passait). En convertissant ici, aucune
 * famille presente ou future ne peut oublier sa conversion.
 *
 * Regles de traversee, volontairement prudentes :
 *   - `Timestamp` -> `Date` ;
 *   - tableaux et maps (objets nus) : traverses recursivement ;
 *   - TOUT LE RESTE (GeoPoint, Bytes, DocumentReference, Date, primitives...)
 *     est rendu tel quel, jamais recopie.
 */
export function timestampsToDates(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate();
  if (Array.isArray(value)) return value.map(timestampsToDates);
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = timestampsToDates(entry);
    }
    return out;
  }
  return value;
}

/**
 * Transforme un patch d'update VALIDE (sortie Zod d'un `XUpdateInputSchema`)
 * en donnees pretes pour `updateDoc`.
 *
 * Regles :
 *   - champ ordinaire : recopie tel quel (`undefined` ignore, patch partiel) ;
 *   - `customFields` : ECLATE en chemins pointes (`customFields.<cle>`) pour
 *     que Firestore FUSIONNE cle par cle au lieu de remplacer la map entiere.
 *     Sans ca, l'app A qui pose `customFields.houseId` effacerait en silence
 *     les cles posees par l'app B (doctrine : familles org-wide partagees,
 *     specialisation par customFields) ;
 *   - une cle de `customFields` a `null` : SUPPRIMEE du document via
 *     `deleteField()` (le schema partage `customFieldValueSchema` documente
 *     deja `null` = champ explicitement vide) ;
 *   - une cle de `customFields` a `undefined` : ignoree.
 *
 * LIMITE (validee ici, fail-fast) : comme la cle est interpolee dans un chemin
 * pointe, elle doit etre un segment de FieldPath valide. Une cle contenant un
 * point ecrirait en silence une map imbriquee (`customFields.a.b`) au lieu de
 * la cle litterale `"a.b"`, et un caractere hors segment (tiret, espace...)
 * ferait lever `updateDoc` a l'execution avec un message obscur. Le helper
 * REFUSE donc toute cle hors `[A-Za-z_][A-Za-z0-9_]*` avec une erreur claire.
 * C'est coherent avec la doctrine : `customFieldDefinitionSchema.key` impose
 * deja `^[a-z][a-zA-Z0-9_]*$` (custom-field.ts, @taki/data-model) ; on tolere
 * juste un peu plus large (majuscule ou underscore en tete) pour rester
 * capable de corriger une cle historique posee a la creation.
 *
 * L'appelant reste responsable d'ajouter `updatedAt` / `updatedBy`.
 */

/** Segment de FieldPath Firestore utilisable sans echappement (cf. commentaire ci-dessus). */
const FIELD_PATH_SEGMENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function buildUpdateData(fields: Record<string, unknown>): DocumentData {
  const data: DocumentData = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v === undefined) continue;
    if (k === 'customFields' && isPlainObject(v)) {
      for (const [key, value] of Object.entries(v)) {
        if (value === undefined) continue;
        if (!FIELD_PATH_SEGMENT.test(key)) {
          throw new Error(
            `buildUpdateData: cle de customFields invalide "${key}" : ` +
              `une cle doit correspondre a [A-Za-z_][A-Za-z0-9_]* ` +
              `(la doctrine impose ^[a-z][a-zA-Z0-9_]*$, cf. customFieldDefinitionSchema.key). ` +
              `Un point creerait une map imbriquee au lieu de la cle litterale.`,
          );
        }
        data[`customFields.${key}`] = value === null ? deleteField() : value;
      }
    } else {
      data[k] = v;
    }
  }
  return data;
}

/**
 * Crée un converter Firestore générique qui :
 *  - injecte l'`id` du document dans l'objet retourné à la lecture,
 *  - normalise TOUS les `Timestamp` du document en `Date` (standard ET métier,
 *    y compris dans les maps et tableaux : cf. `timestampsToDates`),
 *  - délègue la validation/normalisation finale au `parse` fourni (Zod).
 *
 * Le `parse` reçoit l'objet brut (avec `id` et dates converties) et doit
 * renvoyer l'entité typée et validée.
 */
export function createConverter<T>(parse: (raw: DocumentData) => T): FirestoreDataConverter<T> {
  return {
    toFirestore(model: T): DocumentData {
      // Les écritures passent par le repository (qui construit le payload
      // Firestore explicitement) ; on renvoie le modèle tel quel par sécurité.
      return model as unknown as DocumentData;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): T {
      // `serverTimestamps: 'estimate'` : dans un snapshot local (listener temps
      // reel, latence compensee), un serverTimestamp() en attente revient
      // `null` par defaut, et Zod refuserait le document (createdAt non
      // nullable). Avec 'estimate', Firestore renvoie une estimation locale,
      // corrigee au retour serveur. Le temps reel est opt-in par ecran (bible) :
      // on le blinde des maintenant.
      const data = snapshot.data({ ...options, serverTimestamps: 'estimate' });
      const raw: DocumentData = {
        ...(timestampsToDates(data) as DocumentData),
        id: snapshot.id,
        // Les quatre champs de date standard gardent `toDate`, plus tolerant
        // que le walk (il coerce aussi une string/number ISO en Date) : compat
        // stricte avec le comportement historique. `toDate` rend `null` sur une
        // valeur absente, ce qui fait lire un document historique sans
        // `deletedAt` comme un document NON supprime : c'est exactement le sens
        // voulu (le champ arrive avec ce chantier, le parc deja en base ne le
        // porte pas encore).
        createdAt: toDate(data['createdAt']),
        updatedAt: toDate(data['updatedAt']),
        archivedAt: toDate(data['archivedAt']),
        deletedAt: toDate(data['deletedAt']),
      };
      return parse(raw);
    },
  };
}
